import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  /** Optional second line under the headline (e.g. preset description). */
  detail?: string;
  durationMs: number;
  /**
   * Optional call-to-action rendered as a button inside the toast. Used for
   * "Undo" affordances after destructive actions like deletion. The toast
   * stays on screen until `durationMs` elapses OR the user clicks the
   * action — whichever comes first.
   */
  actionLabel?: string;
  onAction?: () => void;
}

export interface ToastPushOptions {
  durationMs?: number;
  actionLabel?: string;
  onAction?: () => void;
  detail?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (kind: ToastKind, message: string, durationOrOptions?: number | ToastPushOptions) => void;
  /** Convenience: success toast. `detailOrOptions` may be a plain string (treated as the second message line) OR a full options object. */
  success: (message: string, detailOrOptions?: string | ToastPushOptions, options?: ToastPushOptions) => void;
  error: (message: string, detailOrOptions?: string | ToastPushOptions, options?: ToastPushOptions) => void;
  info: (message: string, detailOrOptions?: string | ToastPushOptions, options?: ToastPushOptions) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider(props: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback<ToastContextValue['push']>(
    (kind, message, durationOrOptions = {}) => {
      counter.current += 1;
      const id = `toast-${counter.current}`;
      const opts =
        typeof durationOrOptions === 'number'
          ? { durationMs: durationOrOptions }
          : durationOrOptions;
      const toast: Toast = {
        id,
        kind,
        message,
        detail: opts.detail,
        durationMs: opts.durationMs ?? 4500,
        actionLabel: opts.actionLabel,
        onAction: opts.onAction,
      };
      setToasts((prev) => [...prev, toast]);
      if (toast.durationMs > 0) {
        window.setTimeout(() => dismiss(id), toast.durationMs);
      }
    },
    [dismiss],
  );

  // Normalize the v5 caller pattern (message, optionalString, optionalOptions) into
  // a single options object before forwarding to `push`.
  const conv = (_msg: string, a?: string | ToastPushOptions, b?: ToastPushOptions) => {
    const opts: ToastPushOptions =
      a === undefined
        ? b ?? {}
        : typeof a === 'string'
          ? { detail: a, ...(b ?? {}) }
          : a;
    return opts;
  };

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      success: (message, a, b) => push('success', message, conv(message, a, b)),
      error: (message, a, b) => push('error', message, conv(message, a, b)),
      info: (message, a, b) => push('info', message, conv(message, a, b)),
      dismiss,
    }),
    [toasts, push, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {props.children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport(props: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  const { toasts, onDismiss } = props;
  // v6-polish — viewport moved to the top-right corner (was bottom-right).
  // The top-right is the conventional spot for "acknowledge me briefly"
  // notifications and is easier to spot while the user is reading the
  // chat panel. Mobile falls back to a top-spanning strip so the toast
  // doesn't crowd the bottom-edge thumb zone.
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-3 top-3 z-50 flex flex-col items-stretch gap-2 sm:inset-x-auto sm:right-6 sm:top-6 sm:items-end"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.kind === 'error' ? 'alert' : 'status'}
          className={[
            'pointer-events-auto flex max-w-sm items-start gap-3 rounded-lg border p-3 pr-2 shadow-lg backdrop-blur',
            t.kind === 'success'
              ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-50'
              : t.kind === 'error'
                ? 'border-rose-400/40 bg-rose-500/15 text-rose-50'
                : 'border-neutral-400/40 bg-neutral-800/80 text-neutral-50',
          ].join(' ')}
        >
          <span className="flex-1 text-sm leading-snug">
            <span className="block font-semibold">{t.message}</span>
            {t.detail ? (
              <span className="mt-0.5 block text-xs font-normal opacity-90">
                {t.detail}
              </span>
            ) : null}
          </span>
          {t.actionLabel && t.onAction ? (
            <button
              type="button"
              onClick={() => {
                t.onAction?.();
                onDismiss(t.id);
              }}
              className="inline-flex h-11 min-w-[44px] shrink-0 items-center justify-center rounded-md px-2 text-sm font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t.actionLabel}
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(t.id)}
            className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-current opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

/* ──────────────────────────────────────────────────────────────────────
 * v5 a11y — accessible confirm dialog
 *
 * Replacement for `window.confirm()`. Uses the native `<dialog>` element
 * with `showModal()` so focus is trapped, ESC closes, and the browser
 * handles the backdrop. Keeps `role="alertdialog"` semantics for AT and
 * auto-focuses the safe (Cancel) button by default to avoid footgun
 * confirmations.
 * ────────────────────────────────────────────────────────────────────── */

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  const {
    open,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
    onCancel,
  } = props;
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
      // Default-focus the Cancel button so a stray Enter doesn't destroy
      // data. Users must explicitly tab to the destructive action.
      window.setTimeout(() => cancelRef.current?.focus(), 0);
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Keep React state in sync if the user dismisses via ESC / form method=dialog.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    const handleClose = () => onCancel();
    dlg.addEventListener('close', handleClose);
    return () => dlg.removeEventListener('close', handleClose);
  }, [onCancel]);

  return (
    <dialog
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="rounded-xl border border-neutral-200 bg-white p-0 text-neutral-900 shadow-2xl backdrop:bg-neutral-950/60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
      onClick={(e) => {
        // Click on the backdrop closes the dialog. Native <dialog> does
        // not handle this for us — the click target is the dialog itself
        // when the user clicks outside the inner content.
        if (e.target === dialogRef.current) onCancel();
      }}
    >
      <div className="flex flex-col gap-4 p-5 sm:p-6">
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>
        <div className="text-sm text-neutral-700 dark:text-neutral-300">
          {description}
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-4 text-sm font-semibold text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={[
              'inline-flex h-11 cursor-pointer items-center justify-center rounded-md px-4 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
              destructive
                ? 'bg-rose-600 hover:bg-rose-700 focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-900'
                : 'bg-accent-light hover:bg-accent focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-900',
            ].join(' ')}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
